import React from 'react'
import api from 'configs/api'
import { alertSuccess, alertDanger } from 'utils/toast'

interface TimeInterval {
  start: string
  end: string
}

interface WeeklyInterval {
  day_of_week: number
  intervals: TimeInterval[]
}

interface WeeklySchedulePayload {
  specialty_id: string
  specialty_time: number
  user_id: string
  specialty_name: string
  weekly_intervals: WeeklyInterval[]
  start_date: string
  end_date: string
  exception_dates: string[]
  countries: string[]
  groups: string[]
}

export interface SavedWeeklySchedule {
  _id: string
  weekly_intervals: WeeklyInterval[]
  start_date: string
  end_date: string
  exception_dates: string[]
  user_id: string
  specialty_id: string
  slots_ids: string[]
  status: string
  createdAt: string
  updatedAt: string
}

const DAY_OF_WEEK_MAP: { [key: string]: number } = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
}

export const formatDateToAPI = (date: string): string => {
  if (date.length !== 10) return ''
  const [day, month, year] = date.split('/')
  return `${year}-${month}-${day}`
}

export const formatDateFromAPI = (date: string): string => {
  if (!date || date.length < 10) return ''
  const [year, month, day] = date.substring(0, 10).split('-')
  return `${day}/${month}/${year}`
}

export const DAY_OF_WEEK_NAMES: { [key: number]: string } = {
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
  7: 'Domingo',
}

export const fetchWeeklySchedules = async (
  userId: string,
  setSchedules: React.Dispatch<React.SetStateAction<SavedWeeklySchedule[]>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  onComplete?: (schedules: SavedWeeklySchedule[]) => void
): Promise<void> => {
  try {
    const response = await api.get(
      `schedule/v1/schedules/weekly-schedule/user/${userId}`
    )

    if (response.data && Array.isArray(response.data)) {
      setSchedules(response.data)
      if (onComplete) {
        onComplete(response.data)
      }
    } else {
      if (onComplete) {
        onComplete([])
      }
    }
  } catch (err: any) {
    console.error('Erro ao buscar horários:', err)
    if (onComplete) {
      onComplete([])
    }
  } finally {
    setLoading(false)
  }
}

export const formatDayIntervals = (
  weeklyIntervals: WeeklyInterval[],
  dayOfWeek: number
): string => {
  const dayInterval = weeklyIntervals.find((wi) => wi.day_of_week === dayOfWeek)

  if (!dayInterval || dayInterval.intervals.length === 0) {
    return 'Sem horários'
  }

  return dayInterval.intervals
    .map((interval) => `${interval.start} - ${interval.end}`)
    .join(', ')
}

export const deleteWeeklySchedule = async (
  scheduleId: string,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
): Promise<boolean> => {
  setLoading(true)

  try {
    await api.delete(`schedule/v1/schedules/weekly-schedule/${scheduleId}`)
    alertSuccess('Período excluído com sucesso!')
    setLoading(false)
    return true
  } catch (err: any) {
    alertDanger(
      err?.response?.data?.message ||
        'Erro ao excluir período. Tente novamente.'
    )
    setLoading(false)
    return false
  }
}

export type CreateWeeklyScheduleResult = {
  success: boolean
  hasConflict?: boolean
  agendasCreatedCount?: number
}

function readAgendasCreatedCount(data: unknown): number {
  if (!data || typeof data !== 'object') return 0
  const d = data as Record<string, unknown>
  const keys = [
    'agendas_created_count',
    'slots_count',
    'created_count',
    'agendasCreatedCount',
  ] as const
  for (const k of keys) {
    const v = d[k]
    if (typeof v === 'number' && !Number.isNaN(v)) return v
  }
  return 0
}

export const createWeeklySchedule = async (
  payload: WeeklySchedulePayload,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
): Promise<CreateWeeklyScheduleResult> => {
  setLoading(true)

  try {
    const response = await api.post(
      'schedule/v1/schedules/weekly-schedule',
      payload
    )

    if (response.data) {
      alertSuccess('Horários cadastrados com sucesso!')
      setLoading(false)
      return {
        success: true,
        agendasCreatedCount: readAgendasCreatedCount(response.data),
      }
    }

    setLoading(false)
    return { success: false }
  } catch (err: any) {
    alertDanger(
      err?.response?.data?.message ||
        'Erro ao cadastrar horários. Tente novamente.'
    )
    setLoading(false)
    const status = err?.response?.status
    const message = String(err?.response?.data?.message || '').toLowerCase()
    const hasConflict =
      status === 409 ||
      message.includes('conflito') ||
      message.includes('conflict')
    return { success: false, hasConflict }
  }
}

export const buildWeeklySchedulePayload = (
  startDate: string,
  endDate: string,
  daySchedules: { [key: string]: { id: string; from: string; to: string }[] },
  exceptions: {
    id: string
    reason: string
    startDate: string
    startTime: string
    endTime: string
  }[],
  specialty: { id: string; name: string; attendance_time: number },
  userId: string,
  countries: string[],
  groups: string[]
): WeeklySchedulePayload => {
  const weeklyIntervals: WeeklyInterval[] = []

  Object.entries(daySchedules).forEach(([dayKey, slots]) => {
    if (slots.length > 0) {
      const intervals: TimeInterval[] = slots.map((slot) => ({
        start: slot.from,
        end: slot.to,
      }))

      weeklyIntervals.push({
        day_of_week: DAY_OF_WEEK_MAP[dayKey],
        intervals,
      })
    }
  })

  const exceptionDates: string[] = exceptions
    .filter((ex) => ex.startDate.length === 10)
    .map((ex) => formatDateToAPI(ex.startDate))

  return {
    specialty_id: specialty.id,
    specialty_time: specialty.attendance_time || 30,
    user_id: userId,
    specialty_name: specialty.name,
    weekly_intervals: weeklyIntervals,
    start_date: formatDateToAPI(startDate),
    end_date: formatDateToAPI(endDate),
    exception_dates: exceptionDates,
    countries,
    groups,
  }
}
